import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { type ResetPasswordData } from '@shared/schema';
import { getSupabaseClient } from '@/lib/supabase';
import { z } from 'zod';

// Local form schema without token field
const formSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof formSchema>;

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [token, setToken] = useState<string>('');
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  // Get token from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      toast({
        variant: 'destructive',
        title: 'Invalid reset link',
        description: 'The password reset link is invalid or has expired.',
      });
      setLocation('/forgot-password');
    }
  }, [toast, setLocation]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const supabase = await getSupabaseClient();
      
      // When the user comes from the recovery link, Supabase sets a recovery session.
      // We can update the password directly via updateUser.
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      if (!sessionData.session) {
        // If there's no session, redirect them back to the callback to establish it
        toast({
          variant: 'destructive',
          title: 'Session expired',
          description: 'Your reset session has expired. Please use the reset link again.',
        });
        setLocation('/forgot-password');
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: data.password });
      if (error) throw error;

      // Refresh auth context and show success
      await refreshUser();
      setIsSuccess(true);
      toast({
        title: 'Password reset successful',
        description: 'Your password has been updated successfully.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Password reset failed',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-apple-blue via-apple-purple to-apple-pink relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="floating-orb bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
          <div className="floating-orb bg-gradient-to-r from-apple-purple/20 to-apple-pink/20 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
          <div className="floating-orb bg-gradient-to-r from-apple-pink/20 to-apple-orange/20 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-md">
            <Card className="glass-effect border-white/20 shadow-2xl">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold bg-gradient-to-r from-white to-apple-gray bg-clip-text text-transparent">
                  Password Reset Complete
                </CardTitle>
                <CardDescription className="text-white/70">
                  Your password has been successfully updated
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-white/80">
                  You can now sign in to your account with your new password.
                </p>
                <Link href="/login">
                  <Button className="w-full apple-button">
                    Go to Sign In
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-apple-blue via-apple-purple to-apple-pink relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="floating-orb bg-gradient-to-r from-apple-blue/20 to-apple-purple/20 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
        <div className="floating-orb bg-gradient-to-r from-apple-purple/20 to-apple-pink/20 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
        <div className="floating-orb bg-gradient-to-r from-apple-pink/20 to-apple-orange/20 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Back button */}
          <Link href="/login">
            <Button 
              variant="ghost" 
              className="mb-6 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Button>
          </Link>

          <Card className="glass-effect border-white/20 shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-white to-apple-gray bg-clip-text text-transparent">
                Set New Password
              </CardTitle>
              <CardDescription className="text-white/70">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Enter new password"
                              type={showPassword ? 'text' : 'password'}
                              {...field}
                              className="glass-input pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 text-white/60 hover:text-white hover:bg-transparent"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">Confirm New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Confirm new password"
                              type={showConfirmPassword ? 'text' : 'password'}
                              {...field}
                              className="glass-input pr-10"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3 text-white/60 hover:text-white hover:bg-transparent"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full apple-button"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? 'Updating password...' : 'Update Password'}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}